import { useMemo } from 'react';
import { twJoin } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';

import {
  FilterFormProps,
  ObjectSelectFilterProps,
} from '@/models/FilterConfig';
import { FilterPopup } from './FilterPopup';
import SelectFilterHeader from './SelectFilterHeader';

type Props = ObjectSelectFilterProps & FilterFormProps;

export default function ObjectSelectFilter({
  label,
  value,
  options,
  setValues,
}: Props) {
  const { t } = useTranslation('translation', { keyPrefix: 'common' });

  const option = useMemo(() => {
    const find = options.find(
      opt => JSON.stringify(opt.value) === JSON.stringify(value),
    );

    return find?.label;
  }, [value, options]);

  const onClear = () => {
    if (!setValues || !value) return;

    const cleared = Object.keys(value).reduce((acc, key) => {
      acc[key] = undefined;
      return acc;
    }, {} as Record<string, undefined>);

    setValues(cleared);
  };

  return (
    <FilterPopup
      onClear={option ? onClear : undefined}
      label={label}
      reference={
        <SelectFilterHeader
          label={label}
          selected={option ?? t('select').toLowerCase()}
        />
      }
    >
      <ul className="min-h-6 flex flex-col gap-1">
        {options.map(({ label, value }) => (
          <li
            role="button"
            onClick={() => setValues?.(value)}
            key={label}
            className={twJoin(
              'cursor-pointer flex items-center justify-between gap-6 hover:text-primary transition ease-in-out duration-300 text-14',
              label === option ? 'font-600' : '',
            )}
          >
            {label}
          </li>
        ))}
      </ul>
    </FilterPopup>
  );
}
